import { Fragment, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { Terminal } from '../components/visuals/Terminal'

// ── Types & data ─────────────────────────────────────────────────────

interface Recipe {
  id:          string
  featured?:   boolean
  title:       string
  blurb:       string
  tags:        string[]
  author:      string
  read:        string
  updated:     string
  difficulty:  'beginner' | 'intermediate' | 'advanced'
  stack:       string[]
  rows?:       number
  githubPath:  string                  // /<dir-in-instadash-recipes-repo>
  whatItBuilds: string[]               // 3-5 bullets for the generic article
  installCmd:  string                  // single shell command to set up
  runCmd:      string                  // single shell command to run
  keySnippet:  { lang: string; code: string }
}

const GITHUB_BASE = 'https://github.com/instadashio/instadash-recipes/tree/main'

const RECIPES: Recipe[] = [
  {
    id:          'firecrawl-to-instadash',
    featured:    true,
    title:       'Scrape with Firecrawl, stream to Instadash',
    blurb:       'A ~50-line pipeline that crawls a domain, structures every page with Firecrawl, and lands typed rows in an Instadash grid — versioned, queryable, mesh-indexed.',
    tags:        ['firecrawl', 'pipeline', 'ingest', 'web'],
    author:      'instadash · cookbook',
    read:        '8 min read',
    updated:     'just now',
    difficulty:  'intermediate',
    stack:       ['firecrawl', 'node', 'typescript'],
    rows:        28,
    githubPath:  '/firecrawl-to-instadash',
    whatItBuilds: [
      'A live grid at instadash.io/<handle>/hn-top',
      'Auto-inferred schema, time machine seeded with v1',
      'Public mesh entry — agents find it via instamesh_search',
      'Re-runs land as v2, v3 with a diff view',
    ],
    installCmd:  'npm install',
    runCmd:      'npm start',
    keySnippet:  { lang: 'ts', code: '' },
  },
  {
    id:          'claude-mcp-instadash',
    title:       'Connect Claude (and Cursor, Windsurf) via MCP',
    blurb:       'Wire Claude Desktop, Claude.ai web, Cursor, or Windsurf to Instadash with the hosted MCP server. Seven tools, OAuth flow, no API key paste.',
    tags:        ['mcp', 'claude', 'config'],
    author:      'instadash · cookbook',
    read:        '3 min read',
    updated:     'just now',
    difficulty:  'beginner',
    stack:       ['mcp', 'config'],
    githubPath:  '/claude-mcp-instadash',
    whatItBuilds: [
      'A connected MCP client in under 30 seconds',
      'Seven tools available to the agent: push, read, list, schema, versions, edits, mesh search',
      'Hosted OAuth — no key paste in client config',
      'Stdio variant for Claude Desktop documented alongside',
    ],
    installCmd:  '(none — paste a URL in your client)',
    runCmd:      'https://mcp.instadash.io',
    keySnippet:  { lang: 'json', code: '' },
  },
  {
    id:          'langgraph-instadash',
    title:       'LangGraph + Instadash — durable agent, durable data',
    blurb:       'A LangGraph agent that publishes mid-graph output to an editable Instadash grid, interrupts for human review, and reads back the picks before continuing.',
    tags:        ['langgraph', 'python', 'agent', 'hitl'],
    author:      'instadash · cookbook',
    read:        '7 min read',
    updated:     'just now',
    difficulty:  'intermediate',
    stack:       ['langgraph', 'python', 'sqlite'],
    githubPath:  '/langgraph-instadash',
    whatItBuilds: [
      'A two-stage LangGraph agent with interrupt_before for HITL',
      'Editable grid with a checkbox action column',
      'SQLite checkpointer — state persists across process restarts',
      'Agent reads back human edits via /<handle>/<slug>/edits?source=human',
    ],
    installCmd:  'pip install -r requirements.txt',
    runCmd:      'python agent.py --thread-id demo-1',
    keySnippet:  { lang: 'py', code: '' },
  },
  {
    id:          'crewai-instadash',
    title:       'CrewAI + Instadash — typed crew output, persistent surface',
    blurb:       'A two-agent CrewAI workflow that produces validated, typed rows via output_pydantic and pushes them to Instadash in a single HTTP call.',
    tags:        ['crewai', 'python', 'agent', 'pydantic'],
    author:      'instadash · cookbook',
    read:        '5 min read',
    updated:     'just now',
    difficulty:  'intermediate',
    stack:       ['crewai', 'python', 'pydantic'],
    githubPath:  '/crewai-instadash',
    whatItBuilds: [
      'Researcher + Analyst agents in a sequential crew',
      'Output constrained to a Pydantic CompetitorList model',
      'One HTTP push lands the validated rows in a grid',
      'Optional action column lets a human drive the next step',
    ],
    installCmd:  'pip install -r requirements.txt',
    runCmd:      'python crew.py',
    keySnippet:  { lang: 'py', code: '' },
  },
  {
    id:          'pydanticai-instadash',
    title:       'PydanticAI + Instadash — type-safe agent → live grid',
    blurb:       'A PydanticAI agent constrained to a typed result model, with a select-cell action column so the dashboard becomes a CRM-lite the moment the rows land.',
    tags:        ['pydantic-ai', 'python', 'agent', 'types'],
    author:      'instadash · cookbook',
    read:        '5 min read',
    updated:     'just now',
    difficulty:  'intermediate',
    stack:       ['pydantic-ai', 'python', 'anthropic'],
    githubPath:  '/pydanticai-instadash',
    whatItBuilds: [
      'A Lead Pydantic model with 5 typed fields',
      'PydanticAI agent with result_type=LeadList',
      'Grid created with a select action column (outreach status)',
      'Real CRM-lite UX in zero lines of frontend code',
    ],
    installCmd:  'pip install -r requirements.txt',
    runCmd:      'python agent.py',
    keySnippet:  { lang: 'py', code: '' },
  },
  {
    id:          'langchain-mesh-rag',
    title:       'LangChain RAG over the Instadash mesh',
    blurb:       'Plug instamesh_search as a LangChain tool. Your agent retrieves from a corpus of public, structured grids — no vector DB, no embedding pipeline.',
    tags:        ['llm', 'rag', 'langchain', 'python'],
    author:      'instadash · cookbook',
    read:        '6 min read',
    updated:     'just now',
    difficulty:  'intermediate',
    stack:       ['langchain', 'langgraph', 'python'],
    githubPath:  '/langchain-mesh-rag',
    whatItBuilds: [
      'Two LangChain @tool functions: instamesh_search + fetch_grid_rows',
      'A ReAct agent that picks the right grid and fetches rows',
      'No Instadash key required — mesh search is open',
      'Citations baked in via attribution metadata on every hit',
    ],
    installCmd:  'pip install -r requirements.txt',
    runCmd:      'python agent.py',
    keySnippet:  { lang: 'py', code: '' },
  },
  {
    id:          'github-actions-nightly',
    title:       'Nightly grid refresh with GitHub Actions',
    blurb:       'A scheduled workflow that fetches data and pushes a fresh version to an Instadash grid every night. No server, no cron daemon — GitHub runs it.',
    tags:        ['ci', 'github', 'schedule'],
    author:      'instadash · cookbook',
    read:        '4 min read',
    updated:     'just now',
    difficulty:  'beginner',
    stack:       ['github-actions', 'node'],
    githubPath:  '/github-actions-nightly',
    whatItBuilds: [
      'A GitHub Actions workflow that runs at 03:00 UTC daily',
      'Zero-dep Node script (Node 20+ has fetch built in)',
      'Each run creates a new grid version with diff view',
      'Free — fits inside the 2000 free Actions minutes/month',
    ],
    installCmd:  'gh secret set INSTADASH_KEY',
    runCmd:      'gh workflow run "Nightly Instadash refresh"',
    keySnippet:  { lang: 'yaml', code: '' },
  },
  {
    id:          'stripe-webhooks-grid',
    title:       'Stripe webhooks → live revenue grid',
    blurb:       'A Cloudflare Worker that verifies Stripe webhook signatures, normalises events, and lands each one into an Instadash grid your finance team can actually look at.',
    tags:        ['stripe', 'webhooks', 'finance', 'cloudflare'],
    author:      'instadash · cookbook',
    read:        '7 min read',
    updated:     'just now',
    difficulty:  'intermediate',
    stack:       ['cloudflare-workers', 'typescript', 'stripe'],
    githubPath:  '/stripe-webhooks-grid',
    whatItBuilds: [
      'HMAC-SHA256 signature verification (no SDK dependency)',
      'Event normalisation into a stable 7-field schema',
      'Idempotent writes — Stripe retries dedupe on event_id',
      'Local testing via stripe listen + stripe trigger',
    ],
    installCmd:  'npx wrangler secret put INSTADASH_KEY',
    runCmd:      'npm run deploy',
    keySnippet:  { lang: 'ts', code: '' },
  },
  {
    id:          'duckdb-to-instadash',
    title:       'DuckDB query → Instadash grid',
    blurb:       'Run a SQL query against a Parquet, CSV, or remote URL with DuckDB and pipe the result set straight to a versioned Instadash grid. ~3 lines of bash.',
    tags:        ['sql', 'duckdb', 'analytics'],
    author:      'instadash · cookbook',
    read:        '4 min read',
    updated:     'just now',
    difficulty:  'beginner',
    stack:       ['duckdb', 'bash', 'jq'],
    githubPath:  '/duckdb-to-instadash',
    whatItBuilds: [
      'A single shell pipeline: duckdb | jq | instadash push',
      'Reads local files, S3, R2, or remote HTTP Parquet directly',
      'Default demo: top 100 NYC taxi trips by tip percentage',
      'Sub-2-second end-to-end on the demo query',
    ],
    installCmd:  'brew install duckdb jq && npm i -g instadash',
    runCmd:      './query.sh',
    keySnippet:  { lang: 'bash', code: '' },
  },
  {
    id:          'puppeteer-headless-scrape',
    title:       'Puppeteer headless scrape → grid',
    blurb:       'When Firecrawl is not enough: full-browser scraping with login + JS rendering, then push the rows to an Instadash grid.',
    tags:        ['scraping', 'puppeteer', 'browser'],
    author:      'instadash · cookbook',
    read:        '6 min read',
    updated:     'just now',
    difficulty:  'advanced',
    stack:       ['puppeteer', 'node', 'typescript'],
    githubPath:  '/puppeteer-headless-scrape',
    whatItBuilds: [
      'A headless Chromium session with form-based login',
      'DOM extraction via page.evaluate()',
      'Failure-snapshot capture for selector drift',
      'Push to a private grid — credentials never leave the runtime',
    ],
    installCmd:  'npm install',
    runCmd:      'npm start',
    keySnippet:  { lang: 'ts', code: '' },
  },
  {
    id:          'csv-fastapi-upload',
    title:       'Drag-and-drop CSV → FastAPI → grid',
    blurb:       'A 100-line FastAPI service that hosts a drop-zone upload form, parses CSVs, and pushes the rows to Instadash. Drop into any internal tool.',
    tags:        ['fastapi', 'csv', 'internal', 'python'],
    author:      'instadash · cookbook',
    read:        '5 min read',
    updated:     'just now',
    difficulty:  'beginner',
    stack:       ['fastapi', 'python', 'uvicorn'],
    githubPath:  '/csv-fastapi-upload',
    whatItBuilds: [
      'A web form at / with a drop zone for CSV files',
      'Type coercion: numbers, booleans, nulls detected on parse',
      'POST /upload returns a live grid URL',
      'Mounts cleanly inside any existing FastAPI app',
    ],
    installCmd:  'pip install -r requirements.txt',
    runCmd:      'uvicorn main:app --reload',
    keySnippet:  { lang: 'py', code: '' },
  },
]

// Populate keySnippet from a separate const block — keeps the array readable.
const SNIPPETS: Record<string, { lang: string; code: string }> = {
  'firecrawl-to-instadash': { lang: 'ts', code:
`const job = await fc.crawl('https://news.ycombinator.com', {
  limit: 30,
  includePaths: ['/item.*'],
  scrapeOptions: {
    formats: ['extract'],
    extract: { schema: StorySchema, prompt: 'Extract one HN story per page.' },
  },
})

const rows = job.data.map(d => d.extract)

await fetch('https://instadash.io/ingest', {
  method: 'POST',
  headers: {
    Authorization:       \`Bearer \${process.env.INSTADASH_KEY}\`,
    'Content-Type':      'application/json',
    'X-Grid-Name':       'hn-top',
    'X-Grid-Visibility': 'public',
  },
  body: JSON.stringify(rows),
})` },
  'claude-mcp-instadash': { lang: 'json', code:
`{
  "mcpServers": {
    "instadash": {
      "command": "npx",
      "args": ["instadash-mcp"],
      "env": { "INSTADASH_KEY": "sk_..." }
    }
  }
}` },
  'langgraph-instadash': { lang: 'py', code:
`graph = (
  StateGraph(State)
    .add_node("generate", generate_candidates)
    .add_node("publish",  publish_for_review)
    .add_node("read_picks", read_human_picks)
    .add_node("analyse",  deep_analyse)
    .add_edge(START, "generate")
    .add_edge("generate",  "publish")
    .add_edge("publish",   "read_picks")
    .add_edge("read_picks","analyse")
    .add_edge("analyse",   END)
    .compile(checkpointer=sqlite_saver,
             interrupt_before=["read_picks"])
)` },
  'crewai-instadash': { lang: 'py', code:
`class Competitor(BaseModel):
    company: str
    url:     str
    pricing: str
    summary: str

class CompetitorList(BaseModel):
    competitors: List[Competitor]

analyse_task = Task(
    description="Validate pricing + summary for each row.",
    agent=analyst,
    context=[research_task],
    output_pydantic=CompetitorList,   # ← typed contract
)

result = crew.kickoff(inputs={"category": "agent infrastructure"})
push_to_instadash([c.model_dump() for c in result.pydantic.competitors])` },
  'pydanticai-instadash': { lang: 'py', code:
`class Lead(BaseModel):
    company:  str
    domain:   str
    industry: str
    size:     str
    why_fit:  str

agent = Agent(
    "anthropic:claude-opus-4-7",
    result_type=LeadList,
    system_prompt="Generate 8 high-fit example companies for the ICP.",
)

result = agent.run_sync(icp)
push_to_instadash([l.model_dump() for l in result.data.leads])` },
  'langchain-mesh-rag': { lang: 'py', code:
`@tool
def instamesh_search(query: str, k: int = 5) -> list[dict]:
    """Search the Instadash public mesh."""
    qs = urlencode({"q": query, "limit": min(k, 20)})
    with urlopen(f"https://instadash.io/api/mesh/search?{qs}") as r:
        return json.loads(r.read())["results"]

agent = create_react_agent(
    ChatAnthropic(model="claude-opus-4-7"),
    tools=[instamesh_search, fetch_grid_rows],
)` },
  'github-actions-nightly': { lang: 'yaml', code:
`on:
  schedule:
    - cron: '0 3 * * *'           # 03:00 UTC daily
  workflow_dispatch: {}

jobs:
  refresh:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v4
      - uses: actions/setup-node@v4
        with: { node-version: '20' }
      - env: { INSTADASH_KEY: \${{ secrets.INSTADASH_KEY }} }
        run: node refresh.mjs` },
  'stripe-webhooks-grid': { lang: 'ts', code:
`async function verifyStripeSignature(payload, header, secret) {
  const { t, v1 } = Object.fromEntries(header.split(',').map(p => p.split('=')))
  const key = await crypto.subtle.importKey('raw',
    new TextEncoder().encode(secret),
    { name: 'HMAC', hash: 'SHA-256' }, false, ['sign'])
  const sig = await crypto.subtle.sign('HMAC', key,
    new TextEncoder().encode(\`\${t}.\${payload}\`))
  return toHex(sig) === v1   // constant-time in real code
}` },
  'duckdb-to-instadash': { lang: 'bash', code:
`duckdb -json -c "
  SELECT customer_id, SUM(amount) AS lifetime_value
  FROM read_csv_auto('orders.csv')
  GROUP BY 1 ORDER BY 2 DESC LIMIT 500;
" \\
  | jq -c '.[]' \\
  | instadash push --name customer-ltv --public --tags duckdb,sql` },
  'puppeteer-headless-scrape': { lang: 'ts', code:
`const browser = await puppeteer.launch({ headless: true })
const page    = await browser.newPage()

await page.goto('https://news.ycombinator.com/login')
await page.type('input[name="acct"]', HN_USER)
await page.type('input[name="pw"]',   HN_PASS)
await Promise.all([
  page.click('input[type="submit"]'),
  page.waitForNavigation(),
])

const rows = await page.evaluate(() => /* scrape DOM */)
await fetch('https://instadash.io/ingest', { /* … */ })` },
  'csv-fastapi-upload': { lang: 'py', code:
`@app.post("/upload")
async def upload(
    name: Annotated[str, Form()],
    file: Annotated[UploadFile, File()],
):
    rows = [_coerce(r) for r in csv.DictReader(io.StringIO(
        (await file.read()).decode("utf-8")
    ))]
    req = Request("https://instadash.io/ingest",
                  data=json.dumps(rows).encode(), method="POST")
    req.add_header("Authorization", f"Bearer {INSTADASH_KEY}")
    req.add_header("X-Grid-Name", name)
    # ... return grid_url` },
}
for (const r of RECIPES) r.keySnippet = SNIPPETS[r.id] ?? { lang: 'txt', code: '' }


const VIOLET    = '#B79DFF'
const FIRECRAWL = '#FF7A45'

const DIFFICULTY_COLOR: Record<Recipe['difficulty'], { bg: string; fg: string }> = {
  beginner:     { bg: 'rgba(184,255,58,0.1)',  fg: 'var(--lime)' },
  intermediate: { bg: 'rgba(255,179,71,0.1)',  fg: 'var(--warn)' },
  advanced:     { bg: 'rgba(255,92,124,0.1)',  fg: 'var(--err)' },
}

const FEATURED = RECIPES.find(r => r.featured)!
const REST = RECIPES.filter(r => !r.featured)
const ALL_TAGS: string[] = ['all', ...Array.from(new Set(RECIPES.flatMap(r => r.tags)))]

interface PipelineStage {
  id: number
  tag: string
  name: string
  sub: string
  color: string
  detail: string
  io: { in: string; out: string }
}

const PIPELINE_STAGES: PipelineStage[] = [
  { id: 0, tag: '01', name: 'firecrawl',  sub: 'crawl + extract', color: FIRECRAWL,       detail: 'Walks the domain. Returns Markdown + a Zod-typed object per page via the extract API. Skips robots-disallowed paths automatically.', io: { in: 'https://news.ycombinator.com', out: '~30 pages · structured JSON' } },
  { id: 1, tag: '02', name: 'filter',     sub: 'shape rows',      color: 'var(--fg-1)',   detail: 'Filters out pages Firecrawl couldn\'t extract cleanly. Coerces dates, drops empty rows. No glue script — one .map().filter().', io: { in: 'JSON pages', out: 'typed rows' } },
  { id: 2, tag: '03', name: 'http push',  sub: 'POST /ingest',    color: 'var(--accent)', detail: 'Single HTTP call. Authorization bearer, X-Grid-Name header, JSON array body. Server infers schema, atomically lands the version.', io: { in: 'typed rows', out: 'grid · v1 · 28 rows' } },
  { id: 3, tag: '04', name: 'mesh index', sub: 'public + cited',  color: VIOLET,          detail: 'X-Grid-Visibility: public enqueues an indexing job. The grid joins the Instamesh corpus, queryable by any agent via instamesh_search.', io: { in: 'grid · v1', out: 'mesh entry · cite-ready' } },
]

const TLDR_STATS: Array<{ l: string; v: string; sub: string; accent?: boolean }> = [
  { l: 'time to ship',  v: '~5 min', sub: 'including npm i' },
  { l: 'lines of code', v: '~50',    sub: 'one file, zero glue' },
  { l: 'rows landed',   v: '~28',    sub: 'with limit: 30' },
  { l: 'cost',          v: '$0.06',  sub: 'firecrawl free tier', accent: true },
]

const FREE_PERKS: Array<[string, string]> = [
  ['versioned snapshots',  'Every push creates an immutable, time-travelable version. Diff any two from the dashboard.'],
  ['public mesh entry',    'X-Grid-Visibility: public registers the grid. Agents cite it via instamesh_search.'],
  ['schema inferred',      'Server-side type inference on every push. Filters, sparklines, action columns auto-wired.'],
  ['edge-served reads',    'GET /<handle>/<slug>/rows is read-cached on Cloudflare\'s edge — fast and rate-limit-friendly.'],
  ['MCP-callable',         'Drop the MCP server in Claude/Cursor and ask "what changed since yesterday?" against your own grid.'],
]


// ── Index ────────────────────────────────────────────────────────────

export function RecipesIndex() {
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  const [tag, setTag] = useState('all')

  useEffect(() => {
    document.title = 'Recipes · End-to-End Pipelines for Instadash Grids'
  }, [])

  const filtered = useMemo(() => {
    const needle = q.toLowerCase().trim()
    return REST
      .filter(r => tag === 'all' || r.tags.includes(tag))
      .filter(r => !needle || r.title.toLowerCase().includes(needle) || r.blurb.toLowerCase().includes(needle))
  }, [q, tag])

  const openRecipe = (id: string) => navigate('/recipes/' + id)

  return (
    <div data-screen-label="recipes" style={{ padding: '40px 0 80px' }}>
      <div className="container-wide">
        <div className="row" style={{ marginBottom: 18, gap: 14, flexWrap: 'wrap' }}>
          <span className="mono" style={{ fontSize: 11, color: 'var(--accent)', letterSpacing: '0.14em', textTransform: 'uppercase' }}>◆ cookbook</span>
          <span className="mono" style={{ fontSize: 11, color: 'var(--fg-3)' }}>/ recipes</span>
          <span className="mono" style={{ fontSize: 11, color: 'var(--fg-3)' }}>·</span>
          <span className="mono" style={{ fontSize: 11, color: 'var(--lime)' }}>● {RECIPES.length} recipes</span>
        </div>

        <h1 className="h-display" style={{ fontSize: 'clamp(40px, 5vw, 68px)', marginBottom: 18 }}>
          Recipes.
        </h1>
        <p className="lede" style={{ marginBottom: 36, maxWidth: 640 }}>
          End-to-end pipelines other teams have already wired up. Every recipe is real runnable code on GitHub — copy, swap the source, ship a grid in an afternoon.
        </p>

        <FeaturedCard recipe={FEATURED} onOpen={() => openRecipe(FEATURED.id)} />

        <div className="row" style={{ marginTop: 40, marginBottom: 18, gap: 12, flexWrap: 'wrap' }}>
          <div className="mono" style={{ fontSize: 11, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>more recipes</div>
          <span className="spacer"></span>
          <div style={{ position: 'relative', minWidth: 280 }}>
            <span className="mono" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--fg-3)', fontSize: 12 }}>⌕</span>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="search recipes…"
              className="mono"
              style={{
                width: '100%', padding: '7px 12px 7px 28px', fontSize: 12,
                background: 'var(--bg-1)', border: '1px solid var(--line-soft)',
                borderRadius: 6, color: 'var(--fg)', outline: 'none',
              }}
            />
          </div>
        </div>

        <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginBottom: 22 }}>
          {ALL_TAGS.map(t => {
            const active = tag === t
            return (
              <button key={t} onClick={() => setTag(t)} className="mono"
                style={{
                  padding: '5px 11px', fontSize: 11, borderRadius: 4,
                  background: active ? 'var(--accent-soft)' : 'transparent',
                  border: '1px solid ' + (active ? 'var(--accent-line)' : 'var(--line-soft)'),
                  color: active ? 'var(--accent)' : 'var(--fg-2)',
                  cursor: 'pointer',
                }}>
                {t === 'all' ? 'all' : '#' + t}
              </button>
            )
          })}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 14 }}>
          {filtered.map(r => (
            <RecipeCard key={r.id} recipe={r} onOpen={() => openRecipe(r.id)} />
          ))}
          {filtered.length === 0 && (
            <div className="card card-pad" style={{ gridColumn: '1 / -1', textAlign: 'center', color: 'var(--fg-3)' }}>
              <div className="mono" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10 }}>0 recipes</div>
              <div className="h3" style={{ marginBottom: 8, color: 'var(--fg)' }}>Nothing matches.</div>
              <p className="muted" style={{ fontSize: 13, marginBottom: 14 }}>Try a different tag or clear the search.</p>
              <button onClick={() => { setQ(''); setTag('all') }} className="btn btn-ghost btn-mono" style={{ fontSize: 12 }}>
                clear filters
              </button>
            </div>
          )}
        </div>

        <div className="card card-pad" style={{ marginTop: 40, display: 'flex', gap: 22, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 280 }}>
            <div className="eyebrow" style={{ marginBottom: 8 }}>contribute</div>
            <div className="h3" style={{ marginBottom: 4 }}>Got a pipeline worth sharing?</div>
            <div className="muted" style={{ fontSize: 13 }}>
              Open a PR at <a className="mono" style={{ color: 'var(--accent)' }} href={`${GITHUB_BASE.replace('/tree/main', '')}`}>github.com/instadashio/instadash-recipes</a> — we review, test, and credit you.
            </div>
          </div>
          <a href={`${GITHUB_BASE.replace('/tree/main', '')}`} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-mono" style={{ fontSize: 12 }}>↗ open repo</a>
        </div>
      </div>
    </div>
  )
}


// ── Featured card ────────────────────────────────────────────────────

function FeaturedCard({ recipe, onOpen }: { recipe: Recipe; onOpen: () => void }) {
  return (
    <div className="card" style={{
      overflow: 'hidden',
      borderColor: 'var(--accent-line)',
      background: 'linear-gradient(135deg, var(--bg-1) 0%, color-mix(in oklch, var(--accent) 4%, var(--bg-1)) 100%)',
    }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.1fr) minmax(0, 1fr)', minHeight: 320 }}>
        <div style={{ padding: '32px 36px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div>
            <div className="row gap-8" style={{ marginBottom: 14, flexWrap: 'wrap' }}>
              <span className="mono" style={{
                fontSize: 10, padding: '3px 8px', borderRadius: 3,
                background: 'var(--accent-soft)', color: 'var(--accent)',
                border: '1px solid var(--accent-line)',
                textTransform: 'uppercase', letterSpacing: '0.1em',
              }}>★ featured</span>
              <span className="mono" style={{ fontSize: 11, color: 'var(--fg-3)' }}>{recipe.read} · {recipe.difficulty}</span>
            </div>
            <h2 className="h1" style={{ fontSize: 36, marginBottom: 14, letterSpacing: '-0.02em' }}>
              {recipe.title}
            </h2>
            <p className="lede" style={{ fontSize: 15, marginBottom: 20, maxWidth: '52ch' }}>{recipe.blurb}</p>
            <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginBottom: 22 }}>
              {recipe.tags.map(t => (
                <span key={t} className="mono" style={{
                  fontSize: 10, padding: '2px 7px', borderRadius: 3,
                  background: 'var(--bg-2)', border: '1px solid var(--line-soft)',
                  color: 'var(--fg-2)',
                }}>#{t}</span>
              ))}
            </div>
          </div>
          <div className="row gap-8" style={{ flexWrap: 'wrap' }}>
            <button onClick={onOpen} className="btn btn-primary btn-mono" style={{ fontSize: 13, padding: '10px 18px' }}>read recipe →</button>
            <a
              href={`${GITHUB_BASE}${recipe.githubPath}`}
              target="_blank" rel="noopener noreferrer"
              className="btn btn-ghost btn-mono" style={{ fontSize: 13, padding: '10px 16px' }}
            >↗ view on github</a>
          </div>
        </div>

        <div style={{
          padding: 32, borderLeft: '1px solid var(--line-soft)',
          background: 'var(--bg)',
          display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 14,
          minWidth: 0,
        }}>
          <div className="mono" style={{ fontSize: 10, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 4 }}>┌─ pipeline ─┐</div>
          <MiniPipeline />
          <div className="row gap-8" style={{ marginTop: 4, flexWrap: 'wrap' }}>
            <span className="mono" style={{ fontSize: 10, color: 'var(--fg-3)' }}>~50 lines</span>
            <span className="mono" style={{ fontSize: 10, color: 'var(--fg-3)' }}>·</span>
            <span className="mono" style={{ fontSize: 10, color: 'var(--lime)' }}>
              ~{recipe.rows ?? 30} rows · ~5 min end-to-end
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

function MiniPipeline() {
  return (
    <div className="col gap-8">
      {PIPELINE_STAGES.map((s, i) => (
        <div key={s.tag}>
          <div className="row gap-12" style={{
            padding: '10px 14px',
            background: 'var(--bg-1)',
            border: '1px solid var(--line-soft)',
            borderLeft: '2px solid ' + s.color,
            borderRadius: 4,
          }}>
            <span className="mono" style={{ fontSize: 10, color: 'var(--fg-3)' }}>{s.tag}</span>
            <span className="mono" style={{ fontSize: 12, color: s.color, fontWeight: 600 }}>{s.name}</span>
            <span className="spacer"></span>
            <span className="mono" style={{ fontSize: 10, color: 'var(--fg-3)' }}>{s.sub}</span>
          </div>
          {i < PIPELINE_STAGES.length - 1 && (
            <div className="mono" style={{ color: 'var(--fg-3)', fontSize: 10, padding: '2px 0 2px 18px', lineHeight: 1 }}>│</div>
          )}
        </div>
      ))}
    </div>
  )
}


// ── Recipe card ──────────────────────────────────────────────────────

function RecipeCard({ recipe, onOpen }: { recipe: Recipe; onOpen: () => void }) {
  const diff = DIFFICULTY_COLOR[recipe.difficulty]
  return (
    <div onClick={onOpen} className="card card-hover" style={{ padding: 18, cursor: 'pointer', display: 'flex', flexDirection: 'column', minHeight: 200 }}>
      <div className="row gap-8" style={{ marginBottom: 10 }}>
        <span className="mono" style={{ fontSize: 10, color: 'var(--fg-3)' }}>{recipe.read}</span>
        <span className="mono" style={{ fontSize: 10, color: 'var(--fg-3)' }}>·</span>
        <span className="mono" style={{
          fontSize: 9, padding: '2px 6px', borderRadius: 2,
          background: diff.bg, color: diff.fg,
          textTransform: 'uppercase', letterSpacing: '0.08em',
        }}>{recipe.difficulty}</span>
      </div>
      <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8, letterSpacing: '-0.01em' }}>{recipe.title}</div>
      <div className="muted" style={{ fontSize: 12, lineHeight: 1.5, marginBottom: 14, flex: 1 }}>{recipe.blurb}</div>
      <div className="row" style={{ justifyContent: 'space-between', gap: 6, flexWrap: 'wrap' }}>
        <div className="row" style={{ gap: 4, flexWrap: 'wrap' }}>
          {recipe.stack.slice(0, 3).map(s => (
            <span key={s} className="mono" style={{
              fontSize: 9, padding: '2px 6px', borderRadius: 2,
              background: 'var(--bg-2)', border: '1px solid var(--line-soft)',
              color: 'var(--fg-2)',
            }}>{s}</span>
          ))}
        </div>
        <span className="mono" style={{ fontSize: 10, color: 'var(--fg-3)' }}>{recipe.updated}</span>
      </div>
    </div>
  )
}


// ── Recipe article router ────────────────────────────────────────────

export function RecipeArticle() {
  const { recipeId = '' } = useParams<{ recipeId: string }>()
  const recipe = RECIPES.find(r => r.id === recipeId)

  useEffect(() => {
    document.title = recipe
      ? `${recipe.title} · Recipes | Instadash`
      : 'Recipe not found · Recipes | Instadash'
  }, [recipe])

  if (!recipe) {
    return (
      <div style={{ padding: '40px 0 80px' }}>
        <div className="container">
          <Link to="/recipes" className="btn btn-ghost btn-mono" style={{ fontSize: 12, marginBottom: 24, display: 'inline-flex' }}>← all recipes</Link>
          <h1 className="h1">Recipe not found</h1>
          <p className="lede" style={{ marginTop: 14 }}>
            No recipe matches <code className="mono" style={{ color: 'var(--accent)' }}>{recipeId}</code>. It may have been retired or renamed.
          </p>
        </div>
      </div>
    )
  }

  if (recipe.id === 'firecrawl-to-instadash') return <FirecrawlRecipe recipe={recipe} />
  return <GenericRecipe recipe={recipe} />
}


// ── Generic recipe article ───────────────────────────────────────────

function GenericRecipe({ recipe }: { recipe: Recipe }) {
  const navigate = useNavigate()
  const githubUrl = `${GITHUB_BASE}${recipe.githubPath}`

  return (
    <div data-screen-label={`recipe-${recipe.id}`} style={{ padding: '40px 0 80px' }}>
      <div className="container">
        <div className="row gap-8" style={{ marginBottom: 24, fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--fg-3)', flexWrap: 'wrap' }}>
          <Link to="/recipes" style={{ color: 'var(--fg-2)' }}>← cookbook</Link>
          <span>/</span>
          <span style={{ color: 'var(--accent)' }}>{recipe.id}</span>
        </div>

        <div style={{ marginBottom: 36 }}>
          <div className="row gap-8" style={{ marginBottom: 14, flexWrap: 'wrap' }}>
            <span className="mono" style={{ fontSize: 11, color: 'var(--fg-3)' }}>{recipe.read}</span>
            <span className="mono" style={{ fontSize: 11, color: 'var(--fg-3)' }}>·</span>
            <span className="mono" style={{ fontSize: 11, color: 'var(--fg-3)' }}>{recipe.difficulty}</span>
            <span className="mono" style={{ fontSize: 11, color: 'var(--fg-3)' }}>·</span>
            <span className="mono" style={{ fontSize: 11, color: 'var(--fg-3)' }}>updated {recipe.updated}</span>
          </div>
          <h1 className="h1" style={{ marginBottom: 14, maxWidth: 880 }}>{recipe.title}</h1>
          <p className="lede" style={{ maxWidth: 720 }}>{recipe.blurb}</p>
          <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 18 }}>
            {recipe.tags.map(t => (
              <span key={t} className="mono" style={{
                fontSize: 10, padding: '2px 7px', borderRadius: 3,
                background: 'var(--bg-2)', border: '1px solid var(--line-soft)',
                color: 'var(--fg-2)',
              }}>#{t}</span>
            ))}
          </div>
        </div>

        <article style={{ maxWidth: 760 }}>
          <H2>What it builds</H2>
          <ul style={{ paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8, marginTop: 14, marginBottom: 24 }}>
            {recipe.whatItBuilds.map(b => (
              <li key={b} style={{ display: 'grid', gridTemplateColumns: '20px 1fr', gap: 12, padding: '8px 0', fontSize: 14, color: 'var(--fg-1)', lineHeight: 1.6 }}>
                <span className="mono" style={{ color: 'var(--lime)', fontSize: 13 }}>✓</span>
                <span>{b}</span>
              </li>
            ))}
          </ul>

          <H2>The key step</H2>
          <CodeBlock lang={recipe.keySnippet.lang}>{recipe.keySnippet.code}</CodeBlock>

          <Callout tag="note">
            This is the core of the recipe. The full file (including setup, error handling, and the surrounding scaffolding) lives in the GitHub folder linked below — clone or copy it directly.
          </Callout>

          <H2>Run it</H2>
          <CodeBlock lang="bash">{`git clone https://github.com/instadashio/instadash-recipes
cd instadash-recipes${recipe.githubPath}
${recipe.installCmd}
cp .env.example .env       # fill in your keys
${recipe.runCmd}`}</CodeBlock>

          <H2>Stack</H2>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 24 }}>
            {recipe.stack.map(s => (
              <span key={s} className="mono" style={{
                fontSize: 11, padding: '4px 10px', borderRadius: 4,
                background: 'var(--bg-1)', border: '1px solid var(--line-soft)',
                color: 'var(--fg-1)',
              }}>{s}</span>
            ))}
          </div>

          <div className="card card-pad" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 22, flexWrap: 'wrap', marginTop: 14 }}>
            <div style={{ flex: 1, minWidth: 240 }}>
              <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>Full source on GitHub</div>
              <p className="muted" style={{ fontSize: 13 }}>README, runnable code, .env.example, dependencies — all in one folder.</p>
            </div>
            <a
              href={githubUrl}
              target="_blank" rel="noopener noreferrer"
              className="btn btn-primary btn-mono"
              style={{ fontSize: 13, padding: '10px 18px', flexShrink: 0 }}
            >↗ view on github</a>
          </div>
        </article>

        <div style={{ marginTop: 56, paddingTop: 32, borderTop: '1px solid var(--line-soft)' }}>
          <div className="row" style={{ marginBottom: 18 }}>
            <span className="mono" style={{ fontSize: 11, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>─ related recipes</span>
            <span className="spacer"></span>
            <Link to="/recipes" className="mono" style={{ color: 'var(--accent)', fontSize: 11 }}>view all →</Link>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
            {RECIPES.filter(r => r.id !== recipe.id).slice(0, 3).map(r => (
              <RecipeCard key={r.id} recipe={r} onOpen={() => navigate('/recipes/' + r.id)} />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}


// ── Firecrawl → Instadash featured article (rewritten against real APIs) ──

function FirecrawlRecipe({ recipe }: { recipe: Recipe }) {
  const navigate = useNavigate()
  const [activeStage, setActiveStage] = useState(0)
  const githubUrl = `${GITHUB_BASE}${recipe.githubPath}`

  return (
    <div data-screen-label="recipe-firecrawl" style={{ padding: '40px 0 80px' }}>
      <div className="container">
        <div className="row gap-8" style={{ marginBottom: 24, fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--fg-3)', flexWrap: 'wrap' }}>
          <Link to="/recipes" style={{ color: 'var(--fg-2)' }}>← cookbook</Link>
          <span>/</span>
          <span style={{ color: 'var(--fg-2)' }}>ingest</span>
          <span>/</span>
          <span style={{ color: 'var(--accent)' }}>firecrawl-to-instadash</span>
        </div>

        <div style={{ marginBottom: 36 }}>
          <div className="row gap-8" style={{ marginBottom: 14, flexWrap: 'wrap' }}>
            <span className="mono" style={{ fontSize: 10, padding: '3px 8px', borderRadius: 3, background: 'var(--accent-soft)', color: 'var(--accent)', border: '1px solid var(--accent-line)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>★ featured recipe</span>
            <span className="mono" style={{ fontSize: 11, color: 'var(--fg-3)' }}>{recipe.read}</span>
            <span className="mono" style={{ fontSize: 11, color: 'var(--fg-3)' }}>·</span>
            <span className="mono" style={{ fontSize: 11, color: 'var(--fg-3)' }}>{recipe.difficulty}</span>
            <span className="mono" style={{ fontSize: 11, color: 'var(--fg-3)' }}>·</span>
            <span className="mono" style={{ fontSize: 11, color: 'var(--fg-3)' }}>updated {recipe.updated}</span>
          </div>
          <h1 className="h1" style={{ marginBottom: 14, maxWidth: 880 }}>
            Scrape with <span style={{ color: FIRECRAWL }}>Firecrawl</span>, stream to <span style={{ color: 'var(--accent)' }}>Instadash</span>
          </h1>
          <p className="lede" style={{ maxWidth: 720 }}>
            We'll crawl <code className="mono" style={{ color: 'var(--accent)' }}>news.ycombinator.com</code>, extract a typed Story per page with Firecrawl's <code className="mono">extract</code> API, then push the rows into a versioned Instadash grid you can query, share, and let agents cite. End to end in ~50 lines of TypeScript.
          </p>
        </div>

        <PipelineDiagram active={activeStage} setActive={setActiveStage} />

        <div className="card card-pad" style={{ marginTop: 36, marginBottom: 36, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 16 }}>
          {TLDR_STATS.map(s => (
            <div key={s.l}>
              <div className="mono" style={{ fontSize: 10, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>{s.l}</div>
              <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', color: s.accent ? 'var(--lime)' : 'var(--fg)' }}>{s.v}</div>
              <div className="mono" style={{ fontSize: 10, color: 'var(--fg-3)', marginTop: 2 }}>{s.sub}</div>
            </div>
          ))}
        </div>

        <article style={{ maxWidth: 760 }}>
          <H2>Why this combination</H2>
          <P>
            Web scraping is the cheapest data source going, but the output is messy: raw HTML, half-baked structure, no schema, no versioning. <strong style={{ color: 'var(--accent)' }}>Firecrawl</strong> solves the first half — it crawls a domain and returns LLM-friendly Markdown plus a structured object per page when you give it a Zod schema. <strong style={{ color: VIOLET }}>Instadash</strong> solves the second — typed, queryable, versioned, mesh-indexed the second the rows land.
          </P>
          <P>
            Wired together they replace the usual <em>Python script + S3 + Postgres + dashboard build</em> with one ~50-line TypeScript pipeline that hits a single HTTP endpoint to ship. The result is a live grid, a public URL, an MCP-callable endpoint, and a row in the mesh that any agent can find.
          </P>

          <H2><span className="mono" style={{ color: 'var(--accent)' }}>01</span> · Install &amp; auth</H2>
          <P>Two keys — Firecrawl (free tier is enough for the demo) and Instadash. No SDK package required for the Instadash side; the recipe uses plain <code className="mono">fetch</code>.</P>
          <CodeBlock lang="bash">{`# install
npm i @mendable/firecrawl-js zod
npm i -D tsx @types/node

# auth — both keys read from env at runtime
export FIRECRAWL_API_KEY="fc-..."
export INSTADASH_KEY="sk_..."        # mint at https://instadash.io/get-started`}</CodeBlock>

          <H2><span className="mono" style={{ color: 'var(--accent)' }}>02</span> · Define the target schema</H2>
          <P>
            Instadash infers schema server-side on every push, but giving Firecrawl an explicit Zod shape produces cleaner rows and removes "why is this column sometimes null" surprises. Nullable fields stay nullable; required fields stay required.
          </P>
          <CodeBlock lang="ts">{`import { z } from 'zod'

const StorySchema = z.object({
  title:     z.string(),
  url:       z.string().url(),
  author:    z.string().nullable().optional(),
  points:    z.number().nullable().optional(),
  comments:  z.number().nullable().optional(),
  posted_at: z.string().nullable().optional().describe('ISO 8601 timestamp'),
  domain:    z.string().nullable().optional(),
})

type Story = z.infer<typeof StorySchema>`}</CodeBlock>

          <H2><span className="mono" style={{ color: 'var(--accent)' }}>03</span> · Crawl with Firecrawl</H2>
          <P>
            <code className="mono" style={{ color: FIRECRAWL }}>crawl</code> with an <code className="mono">extract</code> block returns one structured object per page that matched the include path. We cap at 30 pages for the demo — set <code className="mono">limit</code> to whatever your plan allows.
          </P>
          <CodeBlock lang="ts">{`import FirecrawlApp from '@mendable/firecrawl-js'

const fc = new FirecrawlApp({ apiKey: process.env.FIRECRAWL_API_KEY! })

const job = await fc.crawl('https://news.ycombinator.com', {
  limit: 30,
  includePaths: ['/item.*'],
  scrapeOptions: {
    formats: ['extract'],
    extract: {
      schema: StorySchema,
      prompt: 'Extract one Hacker News story per page. Skip jobs and ads.',
    },
  },
})

const rows: Story[] = job.data
  .map(d => d.extract)
  .filter((s): s is Story => !!s && 'title' in s && 'url' in s)`}</CodeBlock>

          <H2><span className="mono" style={{ color: 'var(--accent)' }}>04</span> · Push to an Instadash grid</H2>
          <P>
            One HTTP call. <code className="mono">X-Grid-Name</code> is the only required header — everything else is optional metadata that ends up in the mesh.
          </P>
          <CodeBlock lang="ts">{`const res = await fetch('https://instadash.io/ingest', {
  method: 'POST',
  headers: {
    Authorization:        \`Bearer \${process.env.INSTADASH_KEY}\`,
    'Content-Type':       'application/json',
    'X-Grid-Name':        'hn-top',
    'X-Grid-Title':       'Hacker News - top stories',
    'X-Grid-Description': 'Refreshed via Firecrawl.',
    'X-Grid-Tags':        'hn,news,firecrawl',
    'X-Grid-Visibility':  'public',
  },
  body: JSON.stringify(rows),
})

const { grid_url, version } = await res.json()
console.log(grid_url)`}</CodeBlock>

          <Callout tag="note">
            The push is atomic. Each call creates a new version snapshot — re-run the script tomorrow and you get <code className="mono">v2</code> with a diff view, no migration to write.
          </Callout>

          <H2><span className="mono" style={{ color: 'var(--accent)' }}>05</span> · You now have a live grid</H2>
          <P>
            The script writes progress to stderr and the final grid URL to stdout, so you can <code className="mono">open "$(npm start)"</code> from a shell and have it land in the browser.
          </P>

          <FakeTerminal />

          <H2>What you got for free</H2>
          <ul style={{ paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10, marginTop: 14, marginBottom: 22 }}>
            {FREE_PERKS.map(([k, v]) => (
              <li key={k} style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 16, padding: '10px 14px', background: 'var(--bg-1)', border: '1px solid var(--line-soft)', borderRadius: 6 }}>
                <span className="mono" style={{ fontSize: 11, color: 'var(--lime)' }}>✓ {k}</span>
                <span style={{ fontSize: 13, color: 'var(--fg-1)', lineHeight: 1.5 }}>{v}</span>
              </li>
            ))}
          </ul>

          <H2>Going further</H2>
          <P>
            Drop the script into a <Link to="/recipes/github-actions-nightly" style={{ color: 'var(--accent)' }}>GitHub Action</Link> to refresh nightly. Switch to a Cloudflare Worker cron trigger if you need tighter timing. Add an action column with <code className="mono">X-Grid-Actions</code> and a human can mark which stories to summarise next — see the <Link to="/recipes/langgraph-instadash" style={{ color: 'var(--accent)' }}>LangGraph HITL recipe</Link> for the read-back pattern.
          </P>

          <div className="card card-pad" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 22, flexWrap: 'wrap', marginTop: 22 }}>
            <div style={{ flex: 1, minWidth: 240 }}>
              <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>Full source on GitHub</div>
              <p className="muted" style={{ fontSize: 13 }}>The complete runnable file, including the package.json and .env.example.</p>
            </div>
            <a
              href={githubUrl}
              target="_blank" rel="noopener noreferrer"
              className="btn btn-primary btn-mono"
              style={{ fontSize: 13, padding: '10px 18px', flexShrink: 0 }}
            >↗ view on github</a>
          </div>
        </article>

        <div style={{ marginTop: 56, paddingTop: 32, borderTop: '1px solid var(--line-soft)' }}>
          <div className="row" style={{ marginBottom: 18 }}>
            <span className="mono" style={{ fontSize: 11, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>─ related recipes</span>
            <span className="spacer"></span>
            <Link to="/recipes" className="mono" style={{ color: 'var(--accent)', fontSize: 11 }}>view all →</Link>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
            {RECIPES.filter(r => !r.featured).slice(0, 3).map(r => (
              <RecipeCard key={r.id} recipe={r} onOpen={() => navigate('/recipes/' + r.id)} />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}


// ── Pipeline diagram ─────────────────────────────────────────────────

function PipelineDiagram({ active, setActive }: { active: number; setActive: (n: number) => void }) {
  const current = PIPELINE_STAGES[active]

  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <div className="row" style={{ padding: '14px 18px', borderBottom: '1px solid var(--line-soft)', background: 'var(--bg-2)' }}>
        <span className="mono" style={{ fontSize: 11, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>┌─ the pipeline ─┐</span>
        <span className="spacer"></span>
        <span className="mono" style={{ fontSize: 11, color: 'var(--lime)' }}>● live</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr) 18px', padding: '20px 18px', alignItems: 'stretch', gap: 0 }}>
        {PIPELINE_STAGES.map((s, i) => (
          <Fragment key={s.tag}>
            <button onClick={() => setActive(s.id)} style={{
              padding: '14px 12px', textAlign: 'left',
              background: active === s.id ? 'var(--bg-2)' : 'var(--bg-1)',
              border: '1px solid ' + (active === s.id ? s.color : 'var(--line-soft)'),
              borderLeft: '3px solid ' + s.color,
              borderRadius: 6, cursor: 'pointer',
              transition: 'all .15s', position: 'relative',
            }}>
              <div className="mono" style={{ fontSize: 10, color: 'var(--fg-3)', marginBottom: 6 }}>{s.tag}</div>
              <div className="mono" style={{ fontSize: 13, color: s.color, fontWeight: 600, marginBottom: 4 }}>{s.name}</div>
              <div className="mono" style={{ fontSize: 10, color: 'var(--fg-3)' }}>{s.sub}</div>
            </button>
            {i < PIPELINE_STAGES.length - 1 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span className="mono" style={{ color: 'var(--fg-3)', fontSize: 14 }}>→</span>
              </div>
            )}
          </Fragment>
        ))}
      </div>

      <div style={{ padding: '18px 22px', borderTop: '1px solid var(--line-soft)', background: 'var(--bg)' }}>
        <div className="row" style={{ alignItems: 'flex-start', gap: 24, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 260 }}>
            <div className="mono" style={{ fontSize: 10, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>
              stage {current.tag} · {current.name}
            </div>
            <p style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--fg-1)' }}>{current.detail}</p>
          </div>
          <div style={{ minWidth: 260 }}>
            <div className="mono" style={{ fontSize: 10, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>i/o</div>
            <div style={{ background: 'var(--bg-1)', border: '1px solid var(--line-soft)', borderRadius: 4, padding: 10, fontSize: 11, fontFamily: 'var(--mono)' }}>
              <div className="row gap-8" style={{ marginBottom: 6 }}>
                <span style={{ color: 'var(--fg-3)' }}>in </span>
                <span style={{ color: 'var(--fg-1)' }}>{current.io.in}</span>
              </div>
              <div className="row gap-8">
                <span style={{ color: 'var(--fg-3)' }}>out</span>
                <span style={{ color: current.color }}>{current.io.out}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}


// ── Real terminal output (captured from a live run on 2026-05-12) ────

function FakeTerminal() {
  return (
    <div style={{ marginTop: 18, marginBottom: 14 }}>
      <Terminal title="$ npm start">
        <div><span className="ps">$ </span><span className="cmd">npm </span><span className="arg">start</span></div>
        <div><span className="ps">→ </span><span style={{ color: FIRECRAWL }}>firecrawl</span><span className="dim">: starting crawl of news.ycombinator.com</span></div>
        <div><span className="ps">→ </span><span className="dim">extracted </span><span className="num">28</span><span className="dim"> rows</span></div>
        <div><span className="ps">→ </span><span style={{ color: 'var(--accent)' }}>instadash</span><span className="dim">: pushing to grid hn-top</span></div>
        <div><span className="ps">  </span><span className="ok">✓ done · v1</span></div>
        <div><span className="ps">  </span><span className="dim">https://instadash.io/your-handle/hn-top</span></div>
      </Terminal>
    </div>
  )
}


// ── Article primitives ───────────────────────────────────────────────

function H2({ children }: { children: ReactNode }) {
  return <h2 style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.015em', margin: '38px 0 14px', color: 'var(--fg)' }}>{children}</h2>
}

function P({ children }: { children: ReactNode }) {
  return <p style={{ fontSize: 15, lineHeight: 1.7, color: 'var(--fg-1)', marginBottom: 14, maxWidth: '72ch' }}>{children}</p>
}

function CodeBlock({ lang, children }: { lang: string; children: string }) {
  const [copied, setCopied] = useState(false)
  function copy() {
    navigator.clipboard.writeText(children).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1800)
    })
  }
  return (
    <div style={{ marginBottom: 18, border: '1px solid var(--line)', borderRadius: 6, overflow: 'hidden' }}>
      <div className="row" style={{ padding: '7px 14px', background: 'var(--bg-2)', borderBottom: '1px solid var(--line-soft)' }}>
        <span className="mono" style={{ fontSize: 10, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{lang}</span>
        <span className="spacer"></span>
        <span
          className="mono"
          onClick={copy}
          style={{ fontSize: 10, color: copied ? 'var(--accent)' : 'var(--fg-3)', cursor: 'pointer', userSelect: 'none', transition: 'color .15s' }}
        >
          {copied ? '✓ copied' : '⎘ copy'}
        </span>
      </div>
      <pre className="mono" style={{ padding: '14px 18px', background: 'var(--bg)', color: 'var(--fg-1)', fontSize: 12.5, lineHeight: 1.65, overflow: 'auto', margin: 0 }}>{children}</pre>
    </div>
  )
}

type CalloutTag = 'note' | 'tip' | 'warn'

function Callout({ tag = 'note', children }: { tag?: CalloutTag; children: ReactNode }) {
  const colors: Record<CalloutTag, { bg: string; border: string; tag: string }> = {
    note: { bg: 'rgba(0,229,255,0.06)',  border: 'rgba(0,229,255,0.25)',  tag: 'var(--accent)' },
    tip:  { bg: 'rgba(184,255,58,0.06)', border: 'rgba(184,255,58,0.25)', tag: 'var(--lime)' },
    warn: { bg: 'rgba(255,179,71,0.06)', border: 'rgba(255,179,71,0.25)', tag: 'var(--warn)' },
  }
  const c = colors[tag]
  return (
    <div style={{ background: c.bg, border: '1px solid ' + c.border, borderRadius: 6, padding: 14, marginBottom: 18, display: 'flex', gap: 14 }}>
      <span className="mono" style={{ fontSize: 10, color: c.tag, textTransform: 'uppercase', letterSpacing: '0.12em', flexShrink: 0, marginTop: 3 }}>{tag} ▸</span>
      <div style={{ fontSize: 13.5, lineHeight: 1.65, color: 'var(--fg-1)' }}>{children}</div>
    </div>
  )
}
